"use client";

import { useRef, useState } from "react";
import { motion, useReducedMotion } from "motion/react";
import { useTranslations } from "next-intl";
import { Icon } from "@iconify/react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

// Coloration syntaxique (sur fond sombre).
const TOKEN_CLASS = {
    kw: "text-fuchsia-400",
    fn: "text-sky-400",
    str: "text-emerald-400",
    num: "text-amber-300",
    bool: "text-orange-300",
    var: "text-sky-200",
    op: "text-gray-400",
    tag: "text-rose-300",
    attr: "text-violet-300",
    txt: "text-gray-200",
};

export default function ReactLiveState() {
    const t = useTranslations();
    const reduce = useReducedMotion();

    const [likes, setLikes] = useState(0);
    const [following, setFollowing] = useState(false);
    const [flash, setFlash] = useState(null); // "likes" | "following" | null
    const flashTimer = useRef(null);

    function triggerFlash(key) {
        setFlash(key);
        if (flashTimer.current) clearTimeout(flashTimer.current);
        flashTimer.current = setTimeout(() => setFlash(null), 700);
    }

    function like() {
        setLikes((n) => n + 1);
        triggerFlash("likes");
    }

    function toggleFollow() {
        setFollowing((f) => !f);
        triggerFlash("following");
    }

    const followLabel = t("React_live_follow");
    const followingLabel = t("React_live_following");

    // Le code du composant. Les lignes "state" se surlignent à chaque interaction.
    const line = (tokens, state) => ({ tokens, state });
    const CODE = [
        line([["function ", "kw"], ["ProfilCard", "fn"], ["() {", "op"]]),
        line(
            [
                ["  const ", "kw"],
                ["[likes, setLikes]", "var"],
                [" = ", "op"],
                ["useState", "fn"],
                ["(", "op"],
                ["0", "num"],
                [")", "op"],
            ],
            "likes",
        ),
        line(
            [
                ["  const ", "kw"],
                ["[following, setFollowing]", "var"],
                [" = ", "op"],
                ["useState", "fn"],
                ["(", "op"],
                ["false", "bool"],
                [")", "op"],
            ],
            "following",
        ),
        line([]),
        line([["  return", "kw"], [" (", "op"]]),
        line([["    <", "op"], ["div", "tag"], [">", "op"]]),
        line(
            [
                ["      <", "op"],
                ["button", "tag"],
                [" onClick", "attr"],
                ["={() => ", "op"],
                ["setLikes", "fn"],
                ["(likes + ", "op"],
                ["1", "num"],
                [")}>", "op"],
            ],
            "likes",
        ),
        line(
            [
                ["        ❤️ {", "op"],
                ["likes", "var"],
                ["}", "op"],
            ],
            "likes",
        ),
        line([["      </", "op"], ["button", "tag"], [">", "op"]]),
        line([]),
        line(
            [
                ["      <", "op"],
                ["button", "tag"],
                [" onClick", "attr"],
                ["={() => ", "op"],
                ["setFollowing", "fn"],
                ["(!following)}>", "op"],
            ],
            "following",
        ),
        line(
            [
                ["        {", "op"],
                ["following", "var"],
                [" ? ", "op"],
                [`"${followingLabel}"`, "str"],
                [" : ", "op"],
                [`"${followLabel}"`, "str"],
                ["}", "op"],
            ],
            "following",
        ),
        line([["      </", "op"], ["button", "tag"], [">", "op"]]),
        line([["    </", "op"], ["div", "tag"], [">", "op"]]),
        line([["  )", "op"]]),
        line([["}", "op"]]),
    ];

    return (
        <section id="react-live" className="pt-16">
            <h2
                className="text-2xl dark:text-white font-bold font-sourceSans"
                style={{ marginBottom: "0.5em" }}
            >
                {t("React_live_title")}
            </h2>
            <div className="w-20 h-1 bg-primary rounded mt-3 mb-5"></div>

            <p className="text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t("React_live_intro")}
            </p>

            <div className="mt-10 relative rounded-3xl overflow-hidden bg-gradient-to-br from-bgDark-700 via-bgDark-800 to-bgDark-900 ring-1 ring-white/10">
                {/* Glows */}
                <div className="absolute -top-24 -right-24 w-[460px] h-[460px] bg-sky-500/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-20 w-[380px] h-[380px] bg-cyan-500/15 rounded-full blur-3xl pointer-events-none" />

                {/* Barre de fenêtre */}
                <div className="relative flex items-center gap-2 px-5 py-3 border-b border-white/10">
                    <span className="w-3 h-3 rounded-full bg-red-400/90" />
                    <span className="w-3 h-3 rounded-full bg-yellow-400/90" />
                    <span className="w-3 h-3 rounded-full bg-green-400/90" />
                    <span className="ml-3 inline-flex items-center gap-1.5 text-[11px] font-mono text-gray-400">
                        <Icon
                            icon="logos:react"
                            className="w-3.5 h-3.5"
                            aria-hidden="true"
                        />
                        ProfilCard.jsx
                    </span>
                    <span className="ml-auto inline-flex items-center gap-1.5">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                        </span>
                        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-300">
                            {t("React_live_badge")}
                        </span>
                    </span>
                </div>

                <div className="relative grid xl:grid-cols-2">
                    {/* CODE */}
                    <div className="min-w-0 overflow-x-auto p-5 sm:p-7 font-mono text-[12.5px] sm:text-[13px] leading-relaxed border-b xl:border-b-0 xl:border-r border-white/10">
                        {CODE.map((l, i) => (
                            <div
                                key={i}
                                className={`flex rounded -mx-1 px-1 transition-colors duration-300 ${
                                    l.state && l.state === flash
                                        ? "bg-sky-400/[0.13]"
                                        : ""
                                }`}
                            >
                                <span className="select-none w-6 shrink-0 text-right pr-3 text-gray-600">
                                    {i + 1}
                                </span>
                                <span className="whitespace-pre break-words">
                                    {l.tokens.length === 0
                                        ? " "
                                        : l.tokens.map(([text, type], j) => (
                                              <span
                                                  key={j}
                                                  className={TOKEN_CLASS[type]}
                                              >
                                                  {text}
                                              </span>
                                          ))}
                                </span>
                            </div>
                        ))}
                    </div>

                    {/* APERÇU LIVE */}
                    <div className="p-6 sm:p-8 bg-black/20 flex flex-col items-center justify-center gap-6">
                        <span className="text-[10px] uppercase tracking-[0.16em] text-gray-500 self-start flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                            {t("React_live_preview")}
                        </span>

                        {/* Carte de profil interactive */}
                        <div className="w-full max-w-[280px] rounded-2xl bg-white/[0.05] ring-1 ring-white/10 p-5">
                            <div className="flex items-center gap-3 mb-5">
                                <span className="flex items-center justify-center w-11 h-11 rounded-full bg-gradient-to-br from-sky-400 to-cyan-500 text-white font-bold">
                                    {t("React_live_card_initials")}
                                </span>
                                <div className="leading-tight">
                                    <div className="text-white font-semibold text-sm">
                                        {t("React_live_card_name")}
                                    </div>
                                    <div className="text-gray-500 text-xs">
                                        {t("React_live_card_handle")}
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-center gap-2.5">
                                <button
                                    type="button"
                                    onClick={like}
                                    aria-label={t("React_live_like")}
                                    className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.06] hover:bg-rose-500/15 ring-1 ring-white/10 hover:ring-rose-400/40 px-3.5 py-1.5 text-sm text-rose-400 transition active:scale-95"
                                >
                                    <motion.span
                                        key={reduce ? undefined : likes}
                                        initial={
                                            reduce || likes === 0
                                                ? false
                                                : { scale: 1.4 }
                                        }
                                        animate={{ scale: 1 }}
                                        transition={{
                                            type: "spring",
                                            stiffness: 500,
                                            damping: 15,
                                        }}
                                        className="inline-flex"
                                    >
                                        <SolarIcon
                                            name="heart-angle-bold"
                                            className="w-[18px] h-[18px]"
                                        />
                                    </motion.span>
                                    <span className="tabular-nums font-semibold text-gray-200">
                                        {likes}
                                    </span>
                                </button>

                                <button
                                    type="button"
                                    onClick={toggleFollow}
                                    className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-semibold transition active:scale-95 ${
                                        following
                                            ? "bg-emerald-500 text-white"
                                            : "bg-sky-500 hover:bg-sky-400 text-white"
                                    }`}
                                >
                                    {following && (
                                        <SolarIcon
                                            name="check-circle-bold"
                                            className="w-4 h-4"
                                        />
                                    )}
                                    {following ? followingLabel : followLabel}
                                </button>
                            </div>
                        </div>

                        {/* Inspecteur d'état */}
                        <div className="w-full max-w-[280px] font-mono text-[12px]">
                            <div className="text-[10px] uppercase tracking-[0.16em] text-gray-600 mb-2">
                                {t("React_live_state_label")}
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <div
                                    className={`flex items-center justify-between rounded-lg px-3 py-1.5 ring-1 transition-colors duration-300 ${
                                        flash === "likes"
                                            ? "bg-sky-400/10 ring-sky-400/40"
                                            : "bg-white/[0.03] ring-white/10"
                                    }`}
                                >
                                    <span className="text-sky-200">likes</span>
                                    <span className="text-amber-300 font-semibold tabular-nums">
                                        {likes}
                                    </span>
                                </div>
                                <div
                                    className={`flex items-center justify-between rounded-lg px-3 py-1.5 ring-1 transition-colors duration-300 ${
                                        flash === "following"
                                            ? "bg-sky-400/10 ring-sky-400/40"
                                            : "bg-white/[0.03] ring-white/10"
                                    }`}
                                >
                                    <span className="text-sky-200">following</span>
                                    <span className="text-orange-300 font-semibold">
                                        {String(following)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <p className="mt-4 text-sm text-black/60 dark:text-lightBlue/70 max-w-[700px] font-geistSans">
                {t("React_live_caption")}
            </p>
        </section>
    );
}
